
const GEMINI_API_KEY = process.env.REACT_APP_GEMINI_API_KEY;
const GEMINI_MODEL = process.env.REACT_APP_GEMINI_MODEL;
const GEMINI_API_ENDPOINT = process.env.REACT_APP_GEMINI_API_ENDPOINT;


function getSavedUserInfo() {
  try {
    return JSON.parse(localStorage.getItem("myprega_user_info") || "{}");
  } catch (e) {
    return {};
  }
}

function buildUserInfoText(user, language) {
  return `User Info:
- Age: ${user.age || "unknown"}
- Weight: ${user.weight || "unknown"}
- Trimester: ${user.trimester || "unknown"}
- Diet: ${user.diet === "true" || user.veg ? "Vegetarian" : "Non-Vegetarian"}
- Allergies: ${user.allergies || "None"}
- Language: ${language || "hindi"}`;
}


const AI_SYSTEM_PROMPT = `You are a trusted and empathetic pregnancy assistant for Myprega. 
Your role is to guide, support, and educate pregnant women with safe, reliable, and easy-to-understand information in a empathetic and supportive way so it should feel empowered and trusted.

-Never provide or suggest the name of any medication, drug, or brand. 
-Instead, focus on natural care, home remedies, healthy lifestyle tips, and general self-care advice that are safe in pregnancy.
-If a diagnosis is needed, never state it as certain. Use careful language such as “This may be,” “It could possibly be,” or “One possibility is.” Always clarify that these are possibilities based on the user’s inputs.
-Always encourage the user to consult their assigned doctor for any medical decisions, prescriptions, or emergency concerns.
-Tailor your guidance by trimester, risk factors, and cultural diet preferences (e.g., vegetarian, non-vegetarian, regional habits).
-Keep answers clear, structured, and actionable, with practical steps the user can follow.
-If symptoms sound severe, unusual, or prolonged, advise the user to seek immediate clinician contact or emergency care.
-Respond in the user’s preferred language (Hindi, English, Bengali, Tamil, Telugu, Marathi, Gujarati, or Punjabi).
-Do not use markdown formatting (no ###, **, or similar symbols). Keep it plain and text (Bold where it's feel important).
-Always sound human, warm, and supportive — like a caring pregnancy doctor who understands the user’s concerns. 
-Build trust through empathy, reassurance, and encouragement. Avoid sounding robotic or overly technical.`;


const AI_PROMPTS = {
  CHAT: (userInfoText, text, language) => `
${userInfoText}

User question:
${text}

Task: Answer briefly and clearly as a pregnancy-safe assistant in ${language}. Keep it supportive, avoid medical prescriptions, and suggest seeing clinician for definitive advice.
`,

  DIET: (userInfoText, language) => `
${userInfoText}

Task: Create a personalized 1-day pregnancy-safe meal plan considering the user's age, weight and trimester.
Include:
- Breakfast, Mid-morning, Lunch, Evening snack, Dinner
- Approx portion sizes and approximate timings
- Hydration advice
- 2 safe alternatives per meal
- Mention food sources of iron, calcium, and protein
Return the plan as plain text in ${language}. Do NOT include medication names or diagnoses.
`,

  YOGA: (userInfoText, language) => `
${userInfoText}

Task: Provide a safe 20-30 minute prenatal yoga routine tailored to the user's trimester and experience.
Include warm-up, 4-6 poses (with modifications), breathing/pranayama, and cool-down.
Mention safety cues and contraindications. Return plain text in ${language}.
`,

  REPORT: (userInfoText, reportText, language) => `
${userInfoText}

Medical report:
${reportText}

Task: Explain the key values in simple ${language} (no diagnosis). Highlight which values may be outside typical pregnancy ranges and what to discuss with the clinician. End with 3 short questions the user can ask their doctor. Plain text only.
`,
};


function triage(text) {
  const urgentPatterns = [
    /severe pain/i,
    /heavy bleeding/i,
    /fainting/i,
    /no fetal movement/i,
    /chest pain/i,
    /difficulty breathing/i,
    /high fever/i,
    /blurred vision/i,
  ];
  const warnPatterns = [/persistent vomiting/i, /swelling/i, /headache/i, /dizziness/i, /contractions/i, /water breaking/i];

  if (urgentPatterns.some((r) => r.test(text))) {
    return {
      level: "urgent",
      message:
        "ये लक्षण गंभीर हो सकते हैं। कृपया तुरंत अपने डॉक्टर या नज़दीकी आपातकालीन सेवा से संपर्क करें।",
    };
  }
  if (warnPatterns.some((r) => r.test(text))) {
    return {
      level: "warn",
      message:
        "इन लक्षणों के लिए जल्द डॉक्टर से सलाह लेना उचित होगा। फिलहाल आराम और हाइड्रेशन का ध्यान रखें।",
    };
  }
  return { level: "info", message: "" };
}



async function askGemini(systemPrompt, userPrompt, fileBase64 = null, mimeType = null) {
  try {
    const contents = [
      {
        role: "user",
        parts: [
          { text: `${systemPrompt}\n\n${userPrompt}` }
        ],
      },
    ];

    if (fileBase64 && mimeType) {
      contents[0].parts.push({
        inline_data: {
          mime_type: mimeType,
          data: fileBase64.replace(/^data:.*;base64,/, ""),
        },
      });
    }

    const response = await fetch(
      `${GEMINI_API_ENDPOINT}/models/${GEMINI_MODEL}:generateContent?key=${GEMINI_API_KEY}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ contents }),
      }
    );

    const data = await response.json();

    return (
      data?.candidates?.[0]?.content?.parts?.[0]?.text ||
      "❌ No response from Gemini"
    );
  } catch (e) {
    console.error("Gemini Error:", e);
    return "❌ Failed to connect to AI.";
  }
}


export async function chatWithAI({ text, language, patientInfo }) {
  const gate = triage(text);
  if (gate.level === "urgent") return gate.message;

  const saved = patientInfo || getSavedUserInfo();
  const userInfoText = buildUserInfoText(saved, language);
  const prompt = AI_PROMPTS.CHAT(userInfoText, text, language);

  return await askGemini(AI_SYSTEM_PROMPT, prompt);
}

export async function generateDietPlanAI({ patientInfo, language }) {
  const saved = patientInfo || getSavedUserInfo();
  const prompt = AI_PROMPTS.DIET(buildUserInfoText(saved, language), language);

  return await askGemini(AI_SYSTEM_PROMPT, prompt);
}

export async function generateYogaPlanAI({ patientInfo, language }) {
  const saved = patientInfo || getSavedUserInfo();
  const prompt = AI_PROMPTS.YOGA(buildUserInfoText(saved, language), language);

  return await askGemini(AI_SYSTEM_PROMPT, prompt);
}

export async function explainReportAI({ fileBase64, fileType, language, reportText }) {
  const saved = getSavedUserInfo();
  const prompt = AI_PROMPTS.REPORT(buildUserInfoText(saved, language), reportText, language);

  return await askGemini(AI_SYSTEM_PROMPT, prompt, fileBase64, fileType);
}